import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw, Sparkles } from "lucide-react";

const MOCKUP =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/1okp2cgs_the%20present.jpg";
const LOGO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/cirzgmk1_logob.png";
const BOW =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/uqfzourb_masna.png";
const BADGE =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/lnmn4zoh_krug%201.png";
const VIDEO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/3gy9p267_prezent.mp4";

const palette = [
  { name: "Ribbon Red", hex: "#C42127", text: "#fff" },
  { name: "Tissue White", hex: "#FFFFFF", text: "#2B2B2B", border: true },
  { name: "Wrap Grey", hex: "#ECECEC", text: "#2B2B2B" },
  { name: "Charcoal", hex: "#2B2B2B", text: "#fff" },
];

const steps = [
  {
    n: "01",
    title: "Tell us who",
    body: "Friend, significant other, sibling, parent, grandparent, child — or even your boss. One tap sets the recipient and the tone.",
  },
  {
    n: "02",
    title: "Set the occasion & budget",
    body: "Birthday, anniversary, just-because. Pick a budget band and the app quietly narrows the universe of gifts to what actually fits.",
  },
  {
    n: "03",
    title: "Get curated ideas",
    body: "A swipeable shortlist of thoughtful matches — from LEGO flower bouquets to limited sneakers — each with a one-line story of why it works. Save favorites or jump straight to the store.",
  },
];

const bows = [
  { top: "4%", left: "3%", size: 64, rotate: -15, delay: "0s" },
  { top: "12%", right: "6%", size: 48, rotate: 12, delay: "0.8s" },
  { top: "48%", left: "1.5%", size: 40, rotate: 8, delay: "1.6s" },
  { bottom: "10%", right: "3%", size: 72, rotate: -10, delay: "0.4s" },
  { bottom: "4%", left: "12%", size: 44, rotate: 18, delay: "1.2s" },
  { top: "38%", right: "1.5%", size: 36, rotate: -20, delay: "2s" },
];

const recipients = [
  "Friend",
  "Significant other",
  "Sibling",
  "Parent",
  "Child",
  "Boss",
];
const occasions = ["Birthday", "Anniversary", "Just because"];
const budgets = ["$0–$50", "$50–$100", "$100+"];

const giftIdeas = {
  Friend: ["LEGO Flower Bouquet", "Polaroid Go camera", "Concert tickets"],
  "Significant other": [
    "Engraved bracelet",
    "Weekend getaway box",
    "Nike Lebron 21 PRM 'Roses'",
  ],
  Sibling: ["Retro game console", "Custom hoodie", "Board game night set"],
  Parent: ["Engraved photo frame", "Artisan coffee set", "Smart photo display"],
  Child: ["LEGO Creator set", "Mini sneakers", "Storybook subscription"],
  Boss: ["Desk plant terrarium", "Leather notebook", "Specialty tea sampler"],
};

function GiftQuiz() {
  const [stage, setStage] = useState(0);
  const [who, setWho] = useState(null);
  const [occasion, setOccasion] = useState(null);
  const [budget, setBudget] = useState(null);

  const reset = () => {
    setStage(0);
    setWho(null);
    setOccasion(null);
    setBudget(null);
  };

  const gift =
    who && giftIdeas[who][(occasion ? occasions.indexOf(occasion) : 0) % 3];

  const chip = (label, selected, onClick, testid) => (
    <button
      key={label}
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-300 hover:-translate-y-0.5 ${
        selected
          ? "bg-[#C42127] text-white border-[#C42127] shadow-lg shadow-[#C42127]/25"
          : "bg-white text-[#2B2B2B] border-[#E2E2E2] hover:border-[#C42127]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="relative bg-white rounded-3xl border border-[#E2E2E2] shadow-xl shadow-black/5 p-8 md:p-12 max-w-[680px] mx-auto overflow-hidden"
      data-testid="present-gift-quiz"
    >
      <img
        src={BOW}
        alt=""
        aria-hidden="true"
        className="absolute -top-3 -right-3 w-16 rotate-12 opacity-90"
      />
      <AnimatePresence mode="wait">
        {stage === 0 && (
          <motion.div
            key="s0"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35 }}
          >
            <div className="text-xs uppercase tracking-[0.2em] text-[#C42127] font-semibold">
              Step 1 of 3
            </div>
            <h3 className="mt-3 font-present text-2xl md:text-3xl">
              Who are you shopping for?
            </h3>
            <div className="mt-6 flex flex-wrap gap-3">
              {recipients.map((r) =>
                chip(
                  r,
                  who === r,
                  () => setWho(r),
                  `quiz-who-${r.replace(/\s/g, "-").toLowerCase()}`
                )
              )}
            </div>
            <button
              disabled={!who}
              onClick={() => setStage(1)}
              data-testid="quiz-continue-1"
              className="mt-8 px-8 py-3 rounded-full bg-[#C42127] text-white font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#a81b20] transition-colors"
            >
              Continue
            </button>
          </motion.div>
        )}
        {stage === 1 && (
          <motion.div
            key="s1"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35 }}
          >
            <div className="text-xs uppercase tracking-[0.2em] text-[#C42127] font-semibold">
              Step 2 of 3
            </div>
            <h3 className="mt-3 font-present text-2xl md:text-3xl">
              Occasion & budget?
            </h3>
            <div className="mt-6 flex flex-wrap gap-3">
              {occasions.map((o) =>
                chip(
                  o,
                  occasion === o,
                  () => setOccasion(o),
                  `quiz-occasion-${o.replace(/\s/g, "-").toLowerCase()}`
                )
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              {budgets.map((b) =>
                chip(
                  b,
                  budget === b,
                  () => setBudget(b),
                  `quiz-budget-${b.replace(/[^a-z0-9]/gi, "").toLowerCase()}`
                )
              )}
            </div>
            <button
              disabled={!occasion || !budget}
              onClick={() => setStage(2)}
              data-testid="quiz-continue-2"
              className="mt-8 px-8 py-3 rounded-full bg-[#C42127] text-white font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#a81b20] transition-colors"
            >
              Find the present
            </button>
          </motion.div>
        )}
        {stage === 2 && (
          <motion.div
            key="s2"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <img src={BADGE} alt="" className="w-16 mx-auto" />
            <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#9a9a9a] font-medium">
              Perfect gift idea for your {who?.toLowerCase()} ·{" "}
              {occasion?.toLowerCase()} · {budget}
            </div>
            <h3
              className="mt-3 font-present text-3xl md:text-4xl"
              data-testid="quiz-result"
            >
              {gift}
            </h3>
            <p className="mt-3 text-[#6b6b6b]">
              <Sparkles className="w-4 h-4 inline -mt-1 text-[#C42127]" /> A
              thoughtful match — exactly how the real app wraps it up.
            </p>
            <button
              onClick={reset}
              data-testid="quiz-restart"
              className="mt-8 px-8 py-3 rounded-full border border-[#2B2B2B] text-[#2B2B2B] font-medium hover:bg-[#2B2B2B] hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Start over
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CasePresent() {
  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#2B2B2B] font-present-body"
      data-testid="case-present-page"
    >
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#FAFAFA]/90 backdrop-blur-md border-b border-[#E2E2E2]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
          <Link
            to="/#work"
            className="flex items-center gap-2 text-sm font-medium text-[#6b6b6b] hover:text-[#C42127] transition-colors"
            data-testid="present-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All work
          </Link>
          <img
            src={LOGO}
            alt="The Present logo"
            className="h-11 w-auto"
            data-testid="present-nav-logo"
          />
          <Link
            to="/work/rarefig"
            className="hidden md:flex items-center gap-2 text-sm font-medium text-[#6b6b6b] hover:text-[#C42127] transition-colors"
            data-testid="present-next-link"
          >
            Next project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-20 pb-16 md:pt-28 md:pb-20 text-center">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1, type: "spring" }}
              whileHover={{ scale: 1.03, rotate: -1 }}
              src={LOGO}
              alt="The Present — gift finder app"
              className="mx-auto w-[min(420px,70vw)]"
              data-testid="present-hero-logo"
            />
            <h1
              className="mt-10 font-present text-[clamp(34px,5.5vw,68px)] leading-[1.1]"
              data-testid="present-hero-title"
            >
              Find <span className="text-[#C42127]">the</span> perfect{" "}
              <span className="text-[#C42127]">present</span>, every time.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#6b6b6b] max-w-[52ch] mx-auto leading-relaxed">
              A gift-recommendation app that ends gift-stress. Tell it about
              them — it suggests thoughtful gifts they'll love.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {["Mobile App", "Gifting", "UI/UX & Branding", "2025"].map(
                (t) => (
                  <span
                    key={t}
                    className="px-5 py-2 rounded-full bg-white border border-[#E2E2E2] text-sm font-medium text-[#2B2B2B] shadow-sm hover:border-[#C42127] hover:-translate-y-0.5 transition-all duration-300 cursor-default"
                  >
                    {t}
                  </span>
                )
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mockup */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <motion.img
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            src={MOCKUP}
            alt="The Present app screens"
            className="w-full rounded-3xl shadow-2xl shadow-black/10"
            data-testid="present-mockup-image"
          />
        </div>
      </section>

      {/* Meta — gift-tag cards with ribbon → bow on hover */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {[
              ["Role", "UI/UX & Brand Design"],
              ["Year", "2025"],
              ["Platform", "iOS · Android"],
              ["Deliverables", "App design, logo, flows"],
            ].map(([k, v]) => (
              <div
                key={k}
                className="present-meta-card group relative bg-white rounded-2xl border border-[#fadcde] shadow-[0_4px_24px_-12px_rgba(196,33,39,0.35)] pt-14 pb-7 px-6 md:px-7 overflow-visible transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(196,33,39,0.55)] hover:border-[#C42127]"
                data-testid={`present-meta-${k.toLowerCase()}`}
              >
                {/* Ribbon bow (always tied) */}
                <div className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 w-44 h-16">
                  <svg
                    viewBox="0 0 180 60"
                    className="w-full h-full overflow-visible"
                    aria-hidden="true"
                  >
                    {/* left loop */}
                    <path
                      d="M90 22 C 60 4, 46 24, 72 30 C 50 34, 58 50, 90 22 Z"
                      fill="#C42127"
                    />
                    {/* right loop */}
                    <path
                      d="M90 22 C 120 4, 134 24, 108 30 C 130 34, 122 50, 90 22 Z"
                      fill="#C42127"
                    />
                    {/* knot */}
                    <rect
                      x="83"
                      y="17"
                      width="14"
                      height="16"
                      rx="3"
                      fill="#8e1418"
                    />
                    {/* dangling tails */}
                    <path
                      d="M84 33 Q 78 48, 82 62"
                      stroke="#C42127"
                      strokeWidth="5"
                      strokeLinecap="round"
                      fill="none"
                    />
                    <path
                      d="M96 33 Q 102 48, 98 62"
                      stroke="#C42127"
                      strokeWidth="5"
                      strokeLinecap="round"
                      fill="none"
                    />
                    {/* tail notches */}
                    <path d="M82 62 l-3 -6 l6 0 z" fill="#C42127" />
                    <path d="M98 62 l-3 -6 l6 0 z" fill="#C42127" />
                  </svg>
                </div>

                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C42127] font-bold">
                  {k}
                </div>
                <div className="mt-2 font-semibold text-sm md:text-base text-[#1c1c1c] leading-snug">
                  {v}
                </div>
                {/* corner dot accent */}
                <span
                  className="absolute bottom-3 right-3 w-1.5 h-1.5 rounded-full bg-[#C42127] opacity-40 group-hover:opacity-100 transition-opacity duration-500"
                  aria-hidden="true"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In Motion — video */}
      <section className="bg-[#2B2B2B] text-white py-20 md:py-28 overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 flex justify-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="relative w-[min(300px,75vw)]"
            >
              <img
                src={BOW}
                alt=""
                aria-hidden="true"
                className="absolute -top-8 -left-10 w-20 -rotate-12 z-10"
              />
              <div className="rounded-[44px] border-[10px] border-[#1c1c1c] bg-black shadow-[0_0_70px_rgba(196,33,39,0.35)] overflow-hidden">
                <video
                  src={VIDEO}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto block"
                  data-testid="present-demo-video"
                />
              </div>
            </motion.div>
          </div>
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-[0.2em] text-[#ff8a8e] font-semibold">
              In Motion
            </span>
            <h2 className="mt-4 font-present text-3xl md:text-5xl leading-tight">
              Unwrapping the flow, live.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/70">
              A walkthrough of the prototype — from "Start Now" through the
              guided questions to the curated shortlist. Notice how every
              screen keeps a single red action: if it's red, it's the next
              step.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-white/70">
              No menus to learn, no filters to fight. The whole journey reads
              like a conversation with a friend who happens to be great at
              gifts.
            </p>
          </div>
        </div>
      </section>

      {/* Brief + Inspiration */}
      <section className="bg-white border-b border-[#E2E2E2] py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-14">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#C42127] font-semibold">
              The Brief
            </span>
            <h2 className="mt-4 font-present text-3xl md:text-5xl leading-tight">
              No more stressing over what to buy.
            </h2>
            <p className="mt-6 text-lg text-[#6b6b6b] leading-relaxed">
              Everyone knows the spiral: a birthday in three days, twenty
              browser tabs, zero ideas. The Present replaces the spiral with a
              short, friendly conversation — and answers with gifts that feel
              personal, not algorithmic.
            </p>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#C42127] font-semibold">
              Inspiration
            </span>
            <h2 className="mt-4 font-present text-3xl md:text-5xl leading-tight">
              The ribbon does the talking.
            </h2>
            <p className="mt-6 text-lg text-[#6b6b6b] leading-relaxed">
              The identity is built around a single red bow — the universal
              symbol of "this one's for you." The UI stays deliberately
              monochrome and calm, so the gifts themselves provide the color.
              Soft shadows and floating cards echo the feeling of presents
              waiting to be unwrapped.
            </p>
          </div>
        </div>
      </section>

      {/* How it works — steps, with bows scattered across */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {bows.map((b, i) => (
          <img
            key={i}
            src={BOW}
            alt=""
            aria-hidden="true"
            className="present-bow absolute pointer-events-none select-none"
            style={{
              top: b.top,
              left: b.left,
              right: b.right,
              bottom: b.bottom,
              width: b.size,
              rotate: `${b.rotate}deg`,
              animationDelay: b.delay,
            }}
          />
        ))}
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-[#C42127] font-semibold">
              How it works
            </span>
            <h2 className="mt-4 font-present text-3xl md:text-5xl">
              Three steps to the right gift.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                whileHover={{ y: -6 }}
                className="bg-white rounded-3xl border border-[#E2E2E2] p-8 shadow-sm hover:shadow-xl hover:shadow-black/5 transition-shadow duration-500"
                data-testid={`present-step-${s.n}`}
              >
                <div className="font-present text-5xl text-[#C42127]">
                  {s.n}
                </div>
                <h3 className="mt-5 text-xl font-semibold">{s.title}</h3>
                <p className="mt-3 text-[#6b6b6b] leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Try it — interactive quiz */}
      <section className="bg-[#F3F3F3] border-y border-[#E2E2E2] py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[#C42127] font-semibold">
              Try it yourself
            </span>
            <h2 className="mt-4 font-present text-3xl md:text-5xl">
              A tiny taste of the real flow.
            </h2>
            <p className="mt-4 text-[#6b6b6b] max-w-[48ch] mx-auto">
              This mini demo mirrors the app's three-step logic — pick, answer,
              unwrap.
            </p>
          </div>
          <GiftQuiz />
        </div>
      </section>

      {/* Design language */}
      <section className="bg-[#2B2B2B] text-white py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.2em] text-[#ff8a8e] font-semibold">
            Design Language
          </span>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="grid grid-cols-2 gap-4">
                {palette.map((c) => (
                  <motion.div
                    key={c.hex}
                    whileHover={{ scale: 1.04, y: -4 }}
                    className={`rounded-2xl p-6 h-32 flex flex-col justify-between ${
                      c.border ? "border border-white/20" : ""
                    }`}
                    style={{ background: c.hex, color: c.text }}
                    data-testid={`present-swatch-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <span className="font-semibold">{c.name}</span>
                    <span className="font-mono text-sm opacity-70">
                      {c.hex}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="md:col-span-5 bg-white text-[#2B2B2B] rounded-3xl p-8">
              <div className="font-present text-[80px] leading-none">
                Aa<span className="text-[#C42127]">.</span>
              </div>
              <div className="mt-4 font-semibold text-xl">
                Warm serif, quiet sans.
              </div>
              <p className="mt-3 text-[#6b6b6b] leading-relaxed">
                A decorative serif gives the brand its gift-tag charm, while a
                neutral sans keeps flows effortless. One red accent guides
                every decision — if it's red, it's the next step.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer / next */}
      <footer className="py-16 border-t border-[#E2E2E2]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Link
            to="/#work"
            className="flex items-center gap-2 text-sm font-medium text-[#6b6b6b] hover:text-[#C42127] transition-colors"
            data-testid="present-footer-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all work
          </Link>
          <Link
            to="/work/rarefig"
            className="group flex items-center gap-4"
            data-testid="present-footer-next"
          >
            <span className="font-present text-2xl md:text-4xl group-hover:text-[#C42127] transition-colors">
              Next: Rarefig
            </span>
            <ArrowRight className="w-8 h-8 transition-transform duration-300 group-hover:translate-x-2" />
          </Link>
        </div>
      </footer>
    </main>
  );
}
